import { plans, type ProgrammeState, type RepwiseEvent } from '@repwise/domain';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { entryFor, loadIndex, loadPlan, loadShared, storedPlan } from './configs.ts';
import { registerExerciseNames } from './defaults.ts';
import { useRepo } from './hooks.ts';

/**
 * The hosted plan in force, read from the device only (basement rule), and the programme lifecycle state
 * (plans.programmeMode, ADR-0021). `download()` fetches what's missing when there's a connection.
 */
export function useHostedPlan(events: RepwiseEvent[] | null, programme: ProgrammeState | null) {
  const { store } = useRepo();
  const sel = useMemo(() => (events ? plans.activeSelection(events) : null), [events]);
  const [loaded, setLoaded] = useState<{ key: string; plan: plans.PlanConfig | null; table: plans.RpeTable | null; catalogue: plans.ExerciseCatalogue | null } | null>(null);
  const key = sel ? `${sel.planId}@${sel.version}#${sel.variation ?? ''}` : '';
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!sel) return;
    let alive = true;
    void (async () => {
      const plan = (await storedPlan(store, sel.planId!, sel.version ?? 1)) ?? null;
      const { table, catalogue } = await loadShared(store, null);
      if (!alive) return;
      if (catalogue) registerExerciseNames(Object.fromEntries(catalogue.exercises.map((e) => [e.id, e.name])));
      setLoaded({ key, plan: plan ? plans.planFor(plan, sel) : null, table, catalogue }); // the selection's variation (Intermediate)
    })().catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [store, key]); // eslint-disable-line react-hooks/exhaustive-deps

  const ready = sel && loaded?.key === key ? loaded : null;
  const mode = useMemo(() => {
    if (!events) return null;
    return plans.programmeMode(events, (id, v) => (ready?.plan && ready.plan.id === id && ready.plan.version === v && ready.table ? ready.plan : undefined));
  }, [events, ready]);

  const download = useCallback(async () => {
    if (!sel) return;
    setBusy(true);
    setError(null);
    const { index } = await loadIndex(store);
    const entry = entryFor(index, sel.planId!, sel.version ?? 1);
    const r = entry ? await loadPlan(store, entry) : ({ ok: false, error: 'offline' } as const);
    const shared = await loadShared(store, index);
    setBusy(false);
    if (!r.ok || !shared.table) return setError(r.ok ? 'offline' : r.error);
    if (shared.catalogue) registerExerciseNames(Object.fromEntries(shared.catalogue.exercises.map((e) => [e.id, e.name])));
    setLoaded({ key, plan: plans.planFor(r.plan, sel), table: shared.table, catalogue: shared.catalogue });
  }, [store, sel, key]);

  const live = useMemo(() => {
    if (!events || !sel || !ready?.plan || !ready.table || !programme) return null;
    const state = plans.livePlanState(events, sel, ready.plan, ready.table);
    const override = plans.nextOverride(events, sel, ready.plan);
    const swaps = plans.activeSwaps(events, sel); // J18
    return { state, override, swaps, next: plans.planPrescriptions(ready.plan, state, sel.unit ?? programme.settings.unit, programme.settings.gym, ready.catalogue ?? undefined, override, swaps) };
  }, [events, sel, ready, programme]);

  return { selection: sel, mode, plan: ready?.plan ?? null, table: ready?.table ?? null, catalogue: ready?.catalogue ?? null, live, download, busy, error };
}

/** The exercise catalogue stored on this device (kept fresh in the background while online); null until then. */
export function useCatalogue(): plans.ExerciseCatalogue | null {
  const { store } = useRepo();
  const [c, setC] = useState<plans.ExerciseCatalogue | null>(null);
  useEffect(() => {
    let alive = true;
    const use = (cat: plans.ExerciseCatalogue | null | undefined) => {
      if (!alive || !cat) return;
      registerExerciseNames(Object.fromEntries(cat.exercises.map((e) => [e.id, e.name])));
      setC(cat);
    };
    // the stored copy at once (offline), then the shared configs (downloads them the first time online)
    void store.meta<{ hash: string; data: plans.ExerciseCatalogue }>('config:exercises').then((m) => use(m?.data));
    void loadIndex(store)
      .then(({ index }) => loadShared(store, index))
      .then((r) => use(r.catalogue))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [store]);
  return c;
}
